import { Routes, Route, Navigate } from 'react-router';
import { ClientLayoutWrapper } from './components';

import Dashboard from './pages/Dashboard';
import NotFound from './pages/NotFound';

import ServicesListPage from './pages/services/ServicesListPage';
import NewServicePage from './pages/services/NewServicePage';
import CareOrderPage from './pages/services/CareOrderPage';
import EmailSetupOrderPage from './pages/services/EmailSetupOrderPage';
import SiteBoostOrderPage from './pages/services/SiteBoostOrderPage';
import GbpSetupOrderPage from './pages/services/GbpSetupOrderPage';
import StudioStarterOrderPage from './pages/services/StudioStarterOrderPage';
import MyServiceOrderDetailPage from './pages/services/MyServiceOrderDetailPage';
import ServiceDetailLayout from './pages/services/detail/ServiceDetailLayout';
import ServiceOverviewTab from './pages/services/detail/ServiceOverviewTab';
import ServiceLimitsTab from './pages/services/detail/ServiceLimitsTab';
import ServiceAddonsTab from './pages/services/detail/ServiceAddonsTab';
import ServiceBillingTab from './pages/services/detail/ServiceBillingTab';
import ServiceManageTab from './pages/services/detail/ServiceManageTab';

import DomainsListPage from './pages/domains/DomainsListPage';
import WhoisPage from './pages/domains/WhoisPage';
import RegisterDomainPage from './pages/domains/RegisterDomainPage';
import RegisterDomainConfirmPage from './pages/domains/RegisterDomainConfirmPage';
import TransferDomainPage from './pages/domains/TransferDomainPage';
import TransferStatusPage from './pages/domains/transfer/TransferStatusPage';
import DomainIntentProcessingPage from './pages/domains/DomainIntentProcessingPage';
import DomainDetailLayout from './pages/domains/detail/DomainDetailLayout';
import DomainOverviewTab from './pages/domains/detail/DomainOverviewTab';
import DomainContactsTab from './pages/domains/detail/DomainContactsTab';
import DomainNameserversTab from './pages/domains/detail/DomainNameserversTab';
import DomainDnsTab from './pages/domains/detail/DomainDnsTab';
import DomainTransferTab from './pages/domains/detail/DomainTransferTab';

import InvoicesPage from './pages/billing/InvoicesPage';
import InvoiceDetailPage from './pages/billing/InvoiceDetailPage';
import CreditNotesPage from './pages/billing/CreditNotesPage';
import StatementsPage from './pages/billing/StatementsPage';
import PaymentMethodsPage from './pages/billing/PaymentMethodsPage';
import AutoPayPage from './pages/billing/AutoPayPage';

import TicketsListPage from './pages/support/TicketsListPage';
import TicketDetailPage from './pages/support/TicketDetailPage';
import NewTicketPage from './pages/support/NewTicketPage';
import KnowledgebasePage from './pages/support/KnowledgebasePage';

import SettingsLayoutPage from './pages/settings/SettingsLayoutPage';
import SettingsProfilePage from './pages/settings/SettingsProfilePage';
import SettingsSecurityPage from './pages/settings/SettingsSecurityPage';
import SettingsNotificationsPage from './pages/settings/SettingsNotificationsPage';
import SettingsBillingInfoPage from './pages/settings/SettingsBillingInfoPage';
import SettingsActivityPage from './pages/settings/SettingsActivityPage';
import SettingsDataPage from './pages/settings/SettingsDataPage';

/**
 * ClientRouter — route tree for /app/*.
 *
 * Mounted by portal/src/App.tsx behind authentication. Layout chrome (top nav,
 * sub-nav) is supplied by ClientLayoutWrapper via the Outlet pattern.
 */
export function ClientRouter() {
  return (
    <Routes>
      <Route element={<ClientLayoutWrapper />}>
        <Route index element={<Dashboard />} />

        {/* Services */}
        <Route path="services" element={<ServicesListPage />} />
        <Route path="services/new" element={<NewServicePage />} />
        <Route path="order/services/care-plan" element={<CareOrderPage />} />
        <Route path="order/services/email-setup" element={<EmailSetupOrderPage />} />
        <Route path="order/services/site-boost" element={<SiteBoostOrderPage />} />
        <Route path="order/services/gbp-setup" element={<GbpSetupOrderPage />} />
        <Route path="order/services/studio-starter" element={<StudioStarterOrderPage />} />
        {/* Phase 10 service-order detail (Studio Starter / Care / Email Setup).
            Note: more-specific path comes BEFORE /services/:id so the latter
            doesn't match `orders` as an :id and try to load a hosting service. */}
        <Route path="services/orders/:id" element={<MyServiceOrderDetailPage />} />
        <Route path="services/:id" element={<ServiceDetailLayout />}>
          <Route index element={<ServiceOverviewTab />} />
          <Route path="limits" element={<ServiceLimitsTab />} />
          <Route path="addons" element={<ServiceAddonsTab />} />
          <Route path="billing" element={<ServiceBillingTab />} />
          <Route path="manage" element={<ServiceManageTab />} />
        </Route>

        {/* Domains */}
        <Route path="domains" element={<DomainsListPage />} />
        <Route path="domains/whois" element={<WhoisPage />} />
        <Route path="domains/register" element={<RegisterDomainPage />} />
        <Route path="domains/register/:fqdn" element={<RegisterDomainConfirmPage />} />
        <Route path="domains/transfer" element={<TransferDomainPage />} />
        <Route path="domains/transfer/:order_id" element={<TransferStatusPage />} />
        {/* DT-1 (2026-05-11) — domain intent ticket processing page */}
        <Route path="domains/intent/:ticket_id" element={<DomainIntentProcessingPage />} />
        <Route path="domains/:domain" element={<DomainDetailLayout />}>
          <Route index element={<DomainOverviewTab />} />
          <Route path="contacts" element={<DomainContactsTab />} />
          <Route path="nameservers" element={<DomainNameserversTab />} />
          <Route path="dns" element={<DomainDnsTab />} />
          <Route path="transfer" element={<DomainTransferTab />} />
        </Route>

        {/* Billing */}
        <Route path="billing" element={<InvoicesPage />} />
        <Route path="billing/invoices/:id" element={<InvoiceDetailPage />} />
        <Route path="billing/credit-notes" element={<CreditNotesPage />} />
        <Route path="billing/statements" element={<StatementsPage />} />
        <Route path="billing/payment-methods" element={<PaymentMethodsPage />} />
        <Route path="billing/auto-pay" element={<AutoPayPage />} />

        {/* Support */}
        <Route path="support" element={<TicketsListPage />} />
        <Route path="support/new" element={<NewTicketPage />} />
        <Route path="support/:id" element={<TicketDetailPage />} />
        {/* Legacy alias — old nested path still resolves. */}
        <Route
          path="support/tickets/:id"
          element={<Navigate to="/app/support" replace />}
        />

        {/* Knowledgebase — top-level primary-nav slot */}
        <Route path="knowledgebase" element={<KnowledgebasePage />} />
        <Route path="knowledgebase/:category" element={<KnowledgebasePage />} />
        {/* Legacy alias — old nested path still resolves. */}
        <Route
          path="support/knowledgebase"
          element={<Navigate to="/app/knowledgebase" replace />}
        />

        {/* Settings */}
        <Route path="settings" element={<SettingsLayoutPage />}>
          <Route index element={<Navigate to="profile" replace />} />
          <Route path="profile" element={<SettingsProfilePage />} />
          <Route path="security" element={<SettingsSecurityPage />} />
          <Route path="notifications" element={<SettingsNotificationsPage />} />
          <Route path="billing-info" element={<SettingsBillingInfoPage />} />
          <Route path="activity" element={<SettingsActivityPage />} />
          <Route path="data" element={<SettingsDataPage />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
